import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import changeQtyOrdered from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeQtyOrdered";
import updateVariantSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { contactFormStyles } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import SupplyLandProductQuantityOrdered from "@SupplyLand/Components/SupplyLandProductQuantityOrdered";
import VariantProductAddToCartButton from "@SupplyLand/Components/VariantProductAddToCartButton";
import { CustomState } from "@SupplyLand/Store/Data/SupplyLand/SupplyLandReducer";
import ProductDetailsAddToCartButton from "@SupplyLand/Widgets/ProductDetails/SupplyLandAddToCartButton";
import ProductDetailsGrandParentVariantOptions from "@SupplyLand/Widgets/ProductDetails/SupplyLandGrandParentVariantOptions";
import ProductDetailsQuantityOrdered from "@SupplyLand/Widgets/ProductDetails/SupplyLandQuantityOrdered";
import ProductDetailsVariantOptions from "@SupplyLand/Widgets/ProductDetails/SupplyLandVariantOptions";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface VariantTableStyles {
    variantTableRow?: DataTableRowProps;
    basicVariantTableRow?: DataTableRowProps;
    priceCell?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToCartButton?: InjectableCss;
    quantityOrdered?: TextFieldProps;
    addToCartColumn?: InjectableCss;
}

export const variantTableStyles: VariantTableStyles = {
    variantTableRow: {
        css: css`
            background: white;
            a {
                text-decoration: underline;
            }
        `,
        evenRowCss: css`
            background: rgb(251 241 200);
        `,
    },
    basicVariantTableRow: {
        css: css`
            background: white;
            a {
                text-decoration: underline;
            }
        `,
        evenRowCss: css`
            background: white;
        `,
    },
    priceCell: {
        css: css`
            border-left: none !important;
            border-right: none !important;
            background: none;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            color: ${getColor("secondary")};
            box-shadow: 2px 2px 2px rgb(0 0 0 / 60%);
            background: ${getColor("primary")};
            margin-left: 10px;
            border-radius: 8px;
        `,
    },
    productAddToCartButton: {
        css: css`
            color: ${getColor("secondary")};
            box-shadow: 2px 2px 2px rgb(0 0 0 / 60%);
            background: ${getColor("primary")};
            margin-left: 10px;
            border-radius: 8px;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                width: 3.1rem;
                min-width: 3.1rem;
                text-align-last: center;
            `,
        },
    },
};

const styles = variantTableStyles;

const mapStateToProps = (state: CustomState) => {
    return {
        productInfoData: state.pages.productDetails,
        pricing: state.supplyLandData.variantPrices,
        selectedProductId: state.pages.productDetails.selectedProductId,
        variantSelection: state.pages.productDetails.variantSelection,
    };
};

const mapDispatchToProps = {
    changeQtyOrdered,
    updateVariantSelection,
};

interface OwnProps {
    product: ProductModel;
    parentProduct: ProductModel;
    variantChild: ProductModel; // When using a base product, we need one of the variants to load the price/attributes
    priceHeaders: string[];
    isParentChildConnector: boolean;
    attributeNames: string[];
    childrenCount: number;
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;
const VariantRow: React.FC<Props> = props => {
    let priceLoaded = false;
    const parentProduct = props.parentProduct;
    const isParentChildConnector = props.isParentChildConnector;
    // useEffect(() => {
    //     if(props.product.)
    // }, [props.selectedProductId]);

    const getVariantColumns = (child: ProductModel) => {
        if (isParentChildConnector && parentProduct.variantTraits && parentProduct.variantTraits.length > 1) {
            const connectorTrait = child.childTraitValues?.find(trait => trait.value === child.productNumber);
            if (connectorTrait && child.childTraitValues) {
                return (
                    <ProductDetailsGrandParentVariantOptions
                        isTable={true}
                        childTraits={child.childTraitValues}
                        connectorTrait={connectorTrait}
                        optionKeyPart={child.productNumber}
                    ></ProductDetailsGrandParentVariantOptions>
                );
            }
        }
        if (
            !isParentChildConnector &&
            parentProduct.variantTraits !== undefined &&
            parentProduct.variantTraits.length > 0
        ) {
            return <ProductDetailsVariantOptions isTable={true}></ProductDetailsVariantOptions>;
        }
        return null;
    };

    const getAttributeColumns = (child: ProductModel, attributeHeaders: string[]) => {
        if (attributeHeaders.length === 0) {
            return null;
        }

        const attributeColumns = [] as string[];

        let id = child.id;

        if (parentProduct.productNumber === child.productNumber) {
            id = child.id;
        }

        attributeHeaders.forEach(attributeName => {
            const childAttributes = child.attributeTypes;
            if (childAttributes) {
                const attributeColumn = childAttributes.find(
                    (attribute: AttributeTypeModel) => attribute.label === attributeName,
                );
                if (attributeColumn?.attributeValues) {
                    attributeColumns.push(attributeColumn.attributeValues[0].valueDisplay);
                } else {
                    // check variantChild to see if it has the attributes and use them
                    const variantAttributeColumn = props.variantChild?.attributeTypes?.find(
                        (attribute: AttributeTypeModel) => attribute.label === attributeName,
                    );
                    if (variantAttributeColumn && variantAttributeColumn?.attributeValues) {
                        attributeColumns.push(variantAttributeColumn.attributeValues[0].valueDisplay);
                    } else {
                        return null;
                    }
                }
            }
        });
        return attributeColumns.map((attribute, index) => (
            <DataTableCell key={attribute + child.id + index}>{attribute}</DataTableCell>
        ));
    };

    const getPriceColumns = (product: ProductModel) => {
        const childPricing: ProductPriceDto | null = props.pricing[product.id];

        if (!childPricing) {
            return null;
        }

        if (product.minimumOrderQty) {
            props.priceHeaders[0] = "1";
        }

        const priceColumns = [] as string[];

        props.priceHeaders.forEach(priceBreak => {
            priceBreak = priceBreak.replace("+", "");
            const price = childPricing.actualBreakPrices?.find(x => x.breakQty.toString() === priceBreak);
            if (price) {
                priceColumns.push(price.breakPriceDisplay);
            } else {
                const variantChildPricing: ProductPriceDto | null = props.pricing[props.variantChild.id];
                const variantChildPrice = variantChildPricing?.actualBreakPrices?.find(
                    x => x.breakQty.toString() === priceBreak,
                );
                if (variantChildPrice) {
                    priceColumns.push(variantChildPrice.breakPriceDisplay);
                } else {
                    priceColumns.push("N/A");
                }
            }
        });
        priceLoaded = true;
        return priceColumns.map((price, index) => (
            <DataTableCell {...styles.priceCell} key={price + product.id + index}>
                {price}
            </DataTableCell>
        ));
    };

    const variantChildActions = (variantChild: ProductModel) => {
        let productInfo = createFromProduct(variantChild) as ProductInfo;
        if (props.productInfoData.productInfosById) {
            const variantProductInfo = cloneDeep(props.productInfoData.productInfosById[variantChild.id]);
            if (variantProductInfo) {
                productInfo = variantProductInfo;
            }
        }
        const productContext: ProductContextModel = {
            product: variantChild,
            productInfo,
            onQtyOrderedChanged: qtyOrdered => {
                props.changeQtyOrdered({ qtyOrdered, productId: variantChild.id });
            },
            onUnitOfMeasureChanged: unitOfMeasure => {
                productInfo.unitOfMeasure = unitOfMeasure;
            },
        };

        let grandparentSelectionComplete = true;
        const connectorId = parentProduct?.variantTraits?.find(
            variant => variant.nameDisplay === "Parent Child Connector",
        )?.id;
        if (connectorId) {
            parentProduct.variantTraits
                ?.filter(trait => trait.name !== "Parent Child Connector")
                .forEach(trait => {
                    if (
                        props.productInfoData.variantSelection[
                            trait.id +
                                variantChild.childTraitValues?.find(trait => trait.styleTraitId === connectorId)?.value
                        ]
                    ) {
                        grandparentSelectionComplete = true;
                        if (variantChild.properties[props.selectedProductId ?? ""] === props.selectedProductId) {
                            variantChild.id = props.selectedProductId ?? variantChild.id;
                            productContext.productInfo.productId = variantChild.id;
                            productContext.product = variantChild;
                        }
                    } else {
                        grandparentSelectionComplete = false;
                    }
                });
        }

        return (
            <ProductContext.Provider value={productContext}>
                <DataTableCell {...styles.addToCartColumn}>
                    <VisuallyHidden as="label" htmlFor={`${variantChild.id}-qty`} id={`${variantChild.id}-qty-label`}>
                        {translate("QTY")}
                    </VisuallyHidden>
                    <SupplyLandProductQuantityOrdered
                        labelOverride=""
                        extendedStyles={styles.quantityOrdered}
                        variantSelectionCompleted={grandparentSelectionComplete}
                        id={`${variantChild.id}-qty`}
                    />
                    <VariantProductAddToCartButton
                        variantSelectionCompleted={grandparentSelectionComplete}
                        labelOverride="Add"
                        data-test-selector={`actionsAddToCart${variantChild.id}`}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        product={variantChild}
                    />
                </DataTableCell>
            </ProductContext.Provider>
        );
    };

    const rowStyles =
        props.childrenCount < 5
            ? { ...variantTableStyles.basicVariantTableRow }
            : { ...variantTableStyles.variantTableRow };

    return (
        <DataTableRow {...rowStyles} key={props.product.id}>
            <DataTableCell>
                <Link href={props.product.canonicalUrl}>
                    <Typography>{props.product.productNumber}</Typography>
                </Link>
            </DataTableCell>
            {getVariantColumns(props.product)}
            {getAttributeColumns(props.product, props.attributeNames)}
            {getPriceColumns(props.product)}
            {!priceLoaded && (
                <DataTableCell>
                    <LoadingSpinner size={15} />
                </DataTableCell>
            )}
            {isParentChildConnector && variantChildActions(props.product)}
            {!isParentChildConnector && !props.productInfoData.variantSelectionCompleted && (
                <DataTableCell>
                    <ProductQuantityOrdered
                        disabled={true}
                        labelOverride=""
                        extendedStyles={styles.quantityOrdered}
                        variantSelectionCompleted={false}
                        configurationCompleted={true}
                        id={`${props.product.id}-qty`}
                    />
                    <Button disabled={true} {...styles.productAddToCartButton}>
                        {translate("Add")}
                    </Button>
                </DataTableCell>
            )}
            {!isParentChildConnector && props.productInfoData.variantSelectionCompleted && (
                <DataTableCell>
                    <ProductDetailsQuantityOrdered />
                    <ProductDetailsAddToCartButton data-test-selector={`actionsAddToCart${props.product.id}`} />
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(VariantRow);
